import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "react-contexify/ReactContexify.css";
import "leaflet/dist/leaflet.css";
import "./plugins";
import "./styles/index.css";
// import "./garces/styles/garces.css";
// import "./views/TimelineView/styles.css";

import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { AppScreen, appScreenLoader, appScreenShouldRevalidate } from "./routes/app";
import { ErrorView, RouteErrorScreen } from "@data-root/error-boundary";
import { AuthScreen } from "./routes/auth";
import { LibraryScreen, libraryScreenLoader } from "./routes/library";
import { CoreConfig, User } from "@data-root/types";
import { Core } from "@data-root/core";
import { isBrowser } from "react-device-detect";
import { POCKETBASE_BASE_ENDPOINT } from "./config/constants";
import { App as AntdApp, notification } from "antd";
import { ConfigurationScreen, configurationScreenLoader } from "./routes/configuration";
import { ClientContext, IClientContext, useAsyncCallback } from "@data-root/react";
import { createHashRouter, redirect, RouterProvider } from "react-router";
import { AssetsProvider } from "@data-root/react";
import { ViewScreen } from "./routes/app/view";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import markerIcon from "./assets/marker-icon.png";
import * as Sentry from "@sentry/react";
import { AppPermissionsScreen, appConfigurationLoader } from "./routes/app/permissions";
import { DraftsScreen } from "./routes/app/drafts";
import { DashboardPlayground } from "./routes/app/dashboard";
import { experimental_appScreenLoader } from "./routes/_/app";
import { NestedScreen } from "./routes/app/view/nested";
import { ThemeProvider } from "./components/theme-provider";
import { EmbedScreen } from "./routes/embed";
import { useEmbedLogout } from "./hooks/useEmbedLogout";
import { EmbedRefreshResultListener } from "./components/EmbedRefreshResultListener";
import { EmbedNavigateListener } from "./components/EmbedNavigateListener";
import "./i18n";

const sentryCreateHashRouter = Sentry.wrapCreateBrowserRouter(createHashRouter);

export const coreConfig: CoreConfig = {
  internalDriverType: "PocketBase",
  internalEndpoint: POCKETBASE_BASE_ENDPOINT,
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10, // 10 seconds
      gcTime: Infinity,
    },
  },
});
const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

export const core = new Core(coreConfig);
// @ts-expect-error - This is a global variable
window.eigen_core = core;

core.setVariable("__platform__", isBrowser ? "desktop" : "mobile");

const router = sentryCreateHashRouter(
  [
    {
      path: "/embed/",
      element: <EmbedScreen />,
      errorElement: <RouteErrorScreen />,
    },
    {
      path: "/auth/",
      element: <AuthScreen />,
      errorElement: <RouteErrorScreen />,
    },
    {
      path: "/",
      loader: async () => {
        throw redirect("/library");
      },
    },
    {
      path: "/library/",
      loader: libraryScreenLoader,
      element: <LibraryScreen />,
      errorElement: <RouteErrorScreen />,
    },
    {
      path: "/configuration/",
      loader: configurationScreenLoader,
      element: <ConfigurationScreen />,
      errorElement: <RouteErrorScreen />,
      children: [
        {
          path: ":appId/:deploymentId/permissions",
          loader: appConfigurationLoader,
          element: <AppPermissionsScreen />,
        },
      ],
    },
    {
      path: "/app/:appId/:deploymentId/",
      loader: appScreenLoader,
      shouldRevalidate: appScreenShouldRevalidate,
      element: <AppScreen />,
      errorElement: <RouteErrorScreen />,
      children: [
        {
          path: "_",
          children: [
            {
              path: "drafts",
              element: <DraftsScreen />,
            },
            {
              path: "dashboard",
              element: <DashboardPlayground />,
            },
          ],
        },
        {
          path: ":viewId",
          element: <ViewScreen />,
          children: [
            {
              path: ":nestedId",
              element: <NestedScreen />,
            },
          ],
        },
      ],
    },
    {
      path: "/_/app/:appId/:deploymentId/",
      loader: experimental_appScreenLoader,
      shouldRevalidate: appScreenShouldRevalidate,
      element: <AppScreen base="/_/app" />,
      errorElement: <RouteErrorScreen />,
      children: [
        {
          path: "_",
          children: [
            {
              path: "drafts",
              element: <DraftsScreen />,
            },
            {
              path: "dashboard",
              element: <DashboardPlayground />,
            },
          ],
        },
        {
          path: ":viewId",
          element: <ViewScreen />,
          children: [
            {
              path: ":nestedId",
              element: <NestedScreen />,
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_startTransition: true,
      v7_normalizeFormMethod: true,
    },
  }
);

const assets = {
  "custom-marker": markerIcon,
};

const _login = async (user: string, password: string) => {
  try {
    await core.auth(user, password);
    if (!core.user) throw new Error("Invalid credentials.");

    Sentry.setUser({
      id: core.user!.id,
      username: core.user!.name,
      email: core.user!.email,
    });

    await core.save();

    return true;
  } catch (e) {
    return false;
  }
};

const _logout = async () => {
  try {
    await core.clear();
    Sentry.setUser(null);
    return true;
  } catch (error) {
    return false;
  }
};

const _refresh = async () => {
  try {
    await core.refresh();

    return true;
  } catch (error) {
    console.log(error);

    return false;
  }
};

export const App = () => {
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  const [showDevtools, setShowDevtools] = useState(false);

  const [desktop] = useState<boolean>(isBrowser);

  const [user, setUser] = useState<User | undefined>();

  const login = useAsyncCallback(async (user: string, password: string) => {
    const ok = await _login(user, password);

    if (ok) setUser(core.user);

    return ok;
  }, []);

  const logout = useAsyncCallback(async () => {
    const ok = await _logout();

    if (ok) {
      setUser(undefined);
      location.reload();
    } else
      notificationApi.error({
        message: "An unexpected error has occurred",
      });

    return ok;
  }, [notificationApi]);

  const refresh = useAsyncCallback(async () => {
    const ok = await _refresh();

    if (ok) location.reload();
    else
      notificationApi.error({
        message: "An unexpected error has occurred",
      });

    return ok;
  }, [notificationApi]);

  const context = useMemo<IClientContext>(
    () => ({
      core,
      desktop,
      user,
      login,
      refresh,
      logout,
    }),
    [desktop, login, refresh, logout, user]
  );

  useEffect(() => {
    // @ts-expect-error - This is a global variable
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  useEmbedLogout({
    onUserChange: setUser,
    redirectToEmbed: true,
  });

  return (
    <Sentry.ErrorBoundary fallback={ErrorView as any}>
      {notificationContextHolder}

      <QueryClientProvider client={queryClient}>
        {showDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtoolsProduction initialIsOpen={false} buttonPosition="bottom-left" />
          </Suspense>
        )}

        <ThemeProvider>
          <AntdApp style={styles.antdApp}>
            <AssetsProvider assets={assets}>
              <ClientContext.Provider value={context}>
                <EmbedRefreshResultListener />
                <EmbedNavigateListener />
                <RouterProvider router={router} />
              </ClientContext.Provider>
            </AssetsProvider>
          </AntdApp>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

const styles = {
  container: {
    flex: 1,
  },
  antdApp: {
    height: "100%",
    display: "flex",
    flex: 1,
  },
};
